import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { MessageSquare, Send, User } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CollaborationMessages = ({ collaboration, currentUserId }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState('');
  const [messageType, setMessageType] = useState('message'); // 'message' or 'comment'
  const [sending, setSending] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [attachments, setAttachments] = useState([]);

  useEffect(() => {
    fetchMessages();
    // Poll for new messages every 10 seconds
    const interval = setInterval(fetchMessages, 10000);
    return () => clearInterval(interval);
  }, [collaboration.collaboration.id]);

  const fetchMessages = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/collaborations/${collaboration.collaboration.id}/messages`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingFile(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `${API}/collaborations/${collaboration.collaboration.id}/upload`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setAttachments([...attachments, response.data]);
      toast.success('File uploaded');
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error('Failed to upload file');
    } finally {
      setUploadingFile(false);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim()) {
      toast.error('Please enter a message');
      return;
    }

    setSending(true);
    try {
      const token = localStorage.getItem('token');
      const attachmentUrls = attachments.map(a => a.file_url);
      
      await axios.post(
        `${API}/collaborations/${collaboration.collaboration.id}/messages`,
        {
          collaboration_id: collaboration.collaboration.id,
          message_type: messageType,
          content: newMessage,
          attachments: attachmentUrls
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setNewMessage('');
      setAttachments([]);
      toast.success('Message sent');
      fetchMessages();
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center">
            <MessageSquare className="w-5 h-5 mr-2 text-[#62D84E]" />
            Communication
          </div>
          <div className="flex gap-2">
            <button
              className={`px-3 py-1 text-sm rounded ${
                messageType === 'message'
                  ? 'bg-[#62D84E] text-white'
                  : 'bg-gray-200'
              }`}
              onClick={() => setMessageType('message')}
            >
              Messages
            </button>
            <button
              className={`px-3 py-1 text-sm rounded ${
                messageType === 'comment'
                  ? 'bg-[#62D84E] text-white'
                  : 'bg-gray-200'
              }`}
              onClick={() => setMessageType('comment')}
            >
              Comments
            </button>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Messages List */}
        <div className="space-y-4 mb-4 max-h-96 overflow-y-auto">
          {loading ? (
            <div className="text-center py-4">Loading messages...</div>
          ) : messages.length === 0 ? (
            <div className="text-center text-gray-500 py-8">
              No messages yet. Start the conversation!
            </div>
          ) : (
            messages
              .filter((msg) => msg.message_type === messageType)
              .map((msg) => (
                <div
                  key={msg.id}
                  className={`p-3 rounded-lg ${
                    msg.sender_user_id === currentUserId
                      ? 'bg-[#62D84E] text-white ml-8'
                      : 'bg-gray-100 mr-8'
                  }`}
                >
                  <div className="flex items-center mb-1">
                    <User className="w-4 h-4 mr-1" />
                    <span className="font-medium text-sm">
                      {msg.sender_name} ({msg.sender_role})
                    </span>
                    <span className="text-xs ml-auto opacity-75">
                      {new Date(msg.created_at).toLocaleString()}
                    </span>
                  </div>
                  <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                  {msg.attachments && msg.attachments.length > 0 && (
                    <div className="mt-2 text-xs">
                      📎 {msg.attachments.length} attachment(s)
                    </div>
                  )}
                </div>
              ))
          )}
        </div>

        {/* New Message Input */}
        <div className="border-t pt-4">
          <Textarea
            placeholder={`Write a ${messageType}...`}
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            rows={3}
            className="mb-2"
          />
          
          {/* Attachments */}
          {attachments.length > 0 && (
            <div className="mb-2 text-sm text-gray-600">
              {attachments.map((att, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  📎 {att.filename} ({(att.size / 1024).toFixed(1)} KB)
                  <button onClick={() => setAttachments(attachments.filter((_, i) => i !== idx))}>
                    ✕
                  </button>
                </div>
              ))}
            </div>
          )}
          
          <div className="flex justify-between items-center">
            <div>
              <input
                type="file"
                id="file-upload"
                className="hidden"
                onChange={handleFileUpload}
                disabled={uploadingFile}
              />
              <label
                htmlFor="file-upload"
                className="cursor-pointer text-sm text-[#62D84E] hover:underline"
              >
                {uploadingFile ? 'Uploading...' : '📎 Attach File'}
              </label>
            </div>
            <Button
              className="btn-snow"
              onClick={handleSendMessage}
              disabled={sending || !newMessage.trim()}
            >
              <Send className="w-4 h-4 mr-2" />
              Send {messageType === 'message' ? 'Message' : 'Comment'}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default CollaborationMessages;
